import React, { useEffect, useRef, useState } from 'react'
import { createSender } from './webrtc/peerLink'

// The page a phone opens (via the QR from Configure > Inputs) to stream its
// camera to the desktop as a WebRTC sender. One-way: phone -> desktop.
const readRoom = () => {
    try {
        return new URLSearchParams(window.location.search).get('room') || ''
    } catch (e) {
        return ''
    }
}

const STATUS_TEXT = {
    idle: 'Tap “Share camera” to begin',
    requesting: 'Turning on the camera…',
    waiting: 'Waiting for your computer…',
    connecting: 'Connecting…',
    reconnecting: 'Reconnecting…',
    connected: 'Connected — your camera is streaming to the computer',
    disconnected: 'Connection lost',
    failed: 'Connection lost',
    closed: 'Connection closed',
    error: 'Connection error',
    full: 'This code is already in use',
    denied: 'Camera permission is blocked',
    nocamera: 'No camera found',
    busy: 'Camera is busy',
}

// After a connection fails or drops, retry on this cadence. Network changes
// (Wi-Fi off/on, switching networks) leave the OLD connection lingering on the
// signaling server for a short while, so the fresh join is briefly refused as
// "in use" — retrying rides that out until the old one is released.
const RETRY_DELAY_MS = 4000
const MAX_AUTO_RETRIES = 8

// Map a getUserMedia failure to a status + a plain-language reason.
const cameraErrorInfo = (e) => {
    const n = (e && e.name) || ''
    if (n === 'NotAllowedError' || n === 'SecurityError')
        return {
            status: 'denied',
            reason: 'Camera access is blocked for this page.',
        }
    if (
        n === 'NotFoundError' ||
        n === 'DevicesNotFoundError' ||
        n === 'OverconstrainedError'
    )
        return { status: 'nocamera', reason: 'This phone has no usable camera.' }
    if (n === 'NotReadableError' || n === 'TrackStartError')
        return {
            status: 'busy',
            reason: 'Another app is already using the camera.',
        }
    return { status: 'error', reason: (e && e.message) || 'Could not start the camera.' }
}

// Simple, step-by-step help for whatever went wrong — written for a first-time
// user. Each string is one numbered step.
const troubleshootSteps = (status) => {
    if (status === 'full')
        return [
            'This almost always happens right after your phone’s Wi-Fi turned off/on or you changed networks.',
            'Wait about 30 seconds — the previous connection needs time to close by itself.',
            'Tap “Retry”.',
            'Still stuck? On your COMPUTER, tap “Connect with QR code” to get a brand-new code, then scan it again.',
        ]
    if (status === 'denied')
        return [
            'Tap the lock 🔒 or camera icon next to the web address at the top of the screen.',
            'Find “Camera” and set it to “Allow”.',
            'Tap “Retry”.',
        ]
    if (status === 'nocamera')
        return [
            'Make sure this device actually has a working camera.',
            'Fully close this page and open the QR link again.',
            'Tap “Retry”.',
        ]
    if (status === 'busy')
        return [
            'Close any other app using the camera — a video call, the Camera app, another browser tab.',
            'Tap “Retry”.',
        ]
    // Network / dropped connection.
    return [
        'Keep this phone on the SAME Wi-Fi network as your computer.',
        'If your Wi-Fi dropped or you switched networks, just wait a few seconds — it tries again on its own.',
        'Tap “Reconnect” to try right now.',
        'If it keeps failing, get a fresh QR code from your computer and scan it again.',
    ]
}

// The phone's current device orientation, from the reliable Screen Orientation
// API when available, else the viewport shape.
const phoneOrientation = () => {
    try {
        const t =
            (window.screen &&
                window.screen.orientation &&
                window.screen.orientation.type) ||
            ''
        if (t.indexOf('portrait') === 0) return 'portrait'
        if (t.indexOf('landscape') === 0) return 'landscape'
    } catch (e) {
        /* fall through */
    }
    return window.innerHeight >= window.innerWidth ? 'portrait' : 'landscape'
}

const MobileCamera = () => {
    const room = readRoom()
    const videoRef = useRef(null)
    const linkRef = useRef(null)
    const streamRef = useRef(null)
    const [status, setStatus] = useState('idle')
    const [facing, setFacing] = useState('environment') // rear by default
    const [reason, setReason] = useState('')
    const [attempt, setAttempt] = useState(0)
    const [showHelp, setShowHelp] = useState(false)

    // Kept in refs so the auto-retry timer sees live values without re-binding.
    const manualStopRef = useRef(true) // true until the user taps Share
    const attemptRef = useRef(0)
    const retryTimerRef = useRef(null)
    const facingRef = useRef(facing)
    facingRef.current = facing

    const clearRetry = () => {
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current)
            retryTimerRef.current = null
        }
    }
    const teardownLink = () => {
        if (linkRef.current) {
            try {
                linkRef.current.close()
            } catch (e) {
                /* ignore */
            }
            linkRef.current = null
        }
    }

    useEffect(() => {
        return () => {
            clearRetry()
            teardownLink()
            if (streamRef.current)
                streamRef.current.getTracks().forEach((t) => t.stop())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Report device orientation to the desktop so it can auto-rotate the feed.
    useEffect(() => {
        let timer = null
        const push = () => {
            if (linkRef.current && linkRef.current.sendOrient)
                linkRef.current.sendOrient(phoneOrientation())
        }
        const onChange = () => {
            if (timer) clearTimeout(timer)
            timer = setTimeout(push, 200)
        }
        window.addEventListener('orientationchange', onChange)
        window.addEventListener('resize', onChange)
        let so = null
        try {
            so = window.screen && window.screen.orientation
            if (so && so.addEventListener) so.addEventListener('change', onChange)
        } catch (e) {
            /* ignore */
        }
        return () => {
            if (timer) clearTimeout(timer)
            window.removeEventListener('orientationchange', onChange)
            window.removeEventListener('resize', onChange)
            try {
                if (so && so.removeEventListener)
                    so.removeEventListener('change', onChange)
            } catch (e) {
                /* ignore */
            }
        }
    }, [])

    // Seed orientation whenever the link reaches "connected".
    useEffect(() => {
        if (status === 'connected' && linkRef.current && linkRef.current.sendOrient)
            linkRef.current.sendOrient(phoneOrientation())
    }, [status])

    // Open the WebRTC sender using the already-acquired camera stream. A network
    // change makes the browser open a fresh WebSocket (new IP handled for us).
    const connectLink = () => {
        if (!room || !streamRef.current) return
        teardownLink()
        linkRef.current = createSender(room, streamRef.current, {
            onState: handleState,
        })
    }

    const scheduleRetry = () => {
        if (manualStopRef.current || !streamRef.current) return
        if (attemptRef.current >= MAX_AUTO_RETRIES) return // hand over to manual
        clearRetry()
        retryTimerRef.current = setTimeout(() => {
            attemptRef.current += 1
            setAttempt(attemptRef.current)
            setStatus('reconnecting')
            connectLink()
        }, RETRY_DELAY_MS)
    }

    const handleState = (s) => {
        if (s === 'connected') {
            attemptRef.current = 0
            setAttempt(0)
            clearRetry()
            setReason('')
            setStatus('connected')
            return
        }
        if (s === 'connecting') return setStatus('connecting')
        if (s === 'waiting') return setStatus('waiting')
        if (s === 'full') {
            setStatus('full')
            setReason(
                'The previous session is still closing on the server — usually after a Wi-Fi change.'
            )
            scheduleRetry()
            return
        }
        // failed / disconnected / closed / error — the link dropped; auto-retry.
        setStatus(s === 'error' ? 'error' : 'disconnected')
        if (!reason) setReason('The camera stream lost its connection.')
        scheduleRetry()
    }

    // Acquire the camera (only when we don't already hold a live stream — a
    // network change doesn't stop the camera, so reconnects reuse it and never
    // re-prompt for permission).
    const acquireCamera = async () => {
        if (
            streamRef.current &&
            streamRef.current.getVideoTracks().some((t) => t.readyState === 'live')
        )
            return true
        setStatus('requesting')
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: facingRef.current } },
                audio: false,
            })
            streamRef.current = stream
            if (videoRef.current) {
                videoRef.current.srcObject = stream
                videoRef.current.play().catch(() => { })
            }
            setReason('')
            return true
        } catch (e) {
            const info = cameraErrorInfo(e)
            setStatus(info.status)
            setReason(info.reason)
            return false
        }
    }

    // Share / (re)connect. Reused by the Share, Retry and Reconnect buttons.
    const start = async () => {
        if (!room) {
            setStatus('error')
            setReason('No room code — reopen the QR code from your computer.')
            return
        }
        manualStopRef.current = false
        clearRetry()
        const ok = await acquireCamera()
        if (!ok) return
        attemptRef.current = 0
        setAttempt(0)
        setStatus('connecting')
        connectLink()
    }

    // Fully end the session so the user (or a fresh QR) can start clean.
    const disconnect = () => {
        manualStopRef.current = true
        clearRetry()
        teardownLink()
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop())
            streamRef.current = null
        }
        if (videoRef.current) videoRef.current.srcObject = null
        attemptRef.current = 0
        setAttempt(0)
        setReason('')
        setStatus('idle')
    }

    // Flip front/rear. Phones usually allow only ONE camera open at a time, so
    // stop the current track FIRST, then open the other and hot-swap the track.
    const openFacing = async (mode) => {
        try {
            return await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { exact: mode } },
                audio: false,
            })
        } catch (e) {
            return navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: mode } },
                audio: false,
            })
        }
    }

    const switchCamera = async () => {
        const next = facing === 'environment' ? 'user' : 'environment'
        if (streamRef.current)
            streamRef.current.getTracks().forEach((t) => t.stop())
        try {
            const stream = await openFacing(next)
            const newTrack = stream.getVideoTracks()[0]
            if (linkRef.current && linkRef.current.replaceVideoTrack)
                await linkRef.current.replaceVideoTrack(newTrack)
            streamRef.current = stream
            if (videoRef.current) {
                videoRef.current.srcObject = stream
                videoRef.current.play().catch(() => { })
            }
            setFacing(next)
        } catch (e) {
            try {
                const restore = await openFacing(facing)
                const t = restore.getVideoTracks()[0]
                if (linkRef.current && linkRef.current.replaceVideoTrack)
                    await linkRef.current.replaceVideoTrack(t)
                streamRef.current = restore
                if (videoRef.current) {
                    videoRef.current.srcObject = restore
                    videoRef.current.play().catch(() => { })
                }
            } catch (_) {
                /* nothing we can do */
            }
        }
    }

    const connected = status === 'connected'
    const busyState = ['requesting', 'waiting', 'connecting', 'reconnecting'].includes(
        status
    )
    const failState = [
        'disconnected',
        'failed',
        'closed',
        'error',
        'full',
        'denied',
        'nocamera',
        'busy',
    ].includes(status)
    const dotColor = connected
        ? '#4cc9a0'
        : failState
            ? '#e06c6c'
            : '#e0a24c'
    const retryLabel = ['disconnected', 'failed', 'closed'].includes(status)
        ? 'Reconnect'
        : 'Retry'

    return (
        <div className="mc-page" style={styles.page}>
            <style>{`.mc-page::-webkit-scrollbar{width:0;height:0;display:none}`}</style>
            <div style={styles.card}>
                <h1 style={styles.title}>Phone Camera</h1>
                <p style={styles.room}>
                    {room ? (
                        <>
                            Room <strong>{room}</strong>
                        </>
                    ) : (
                        'Missing room code — reopen the QR from your computer.'
                    )}
                </p>

                <div style={styles.videoWrap}>
                    <video
                        ref={videoRef}
                        style={styles.video}
                        autoPlay
                        playsInline
                        muted
                    />
                    <span style={{ ...styles.dot, background: dotColor }} />
                </div>

                <p style={styles.status}>{STATUS_TEXT[status] || status}</p>
                {busyState && attempt > 0 && (
                    <p style={styles.subStatus}>Reconnect attempt {attempt}…</p>
                )}
                {reason && failState && <p style={styles.reason}>{reason}</p>}

                {status === 'idle' ? (
                    <button style={styles.btn} onClick={start} disabled={!room}>
                        Share camera
                    </button>
                ) : connected ? (
                    <div style={styles.btnRow}>
                        <button style={styles.btnAlt} onClick={switchCamera}>
                            Switch camera ({facing === 'environment'
                                ? 'rear'
                                : 'front'})
                        </button>
                        <button style={styles.btnDanger} onClick={disconnect}>
                            Disconnect
                        </button>
                    </div>
                ) : (
                    <div style={styles.btnRow}>
                        {failState && (
                            <button style={styles.btn} onClick={start} disabled={!room}>
                                {retryLabel}
                            </button>
                        )}
                        <button style={styles.btnDanger} onClick={disconnect}>
                            Disconnect
                        </button>
                    </div>
                )}

                {status !== 'idle' && (
                    <button
                        style={styles.helpToggle}
                        onClick={() => setShowHelp((v) => !v)}
                    >
                        {showHelp ? 'Hide troubleshooting' : 'Having trouble?'}
                    </button>
                )}

                {showHelp && status !== 'idle' && (
                    <div style={styles.help}>
                        <p style={styles.helpTitle}>Try these steps</p>
                        <ol style={styles.helpList}>
                            {troubleshootSteps(status).map((s, i) => (
                                <li key={i} style={styles.helpItem}>
                                    {s}
                                </li>
                            ))}
                        </ol>
                    </div>
                )}

                {connected && (
                    <p style={styles.hint}>
                        Keep this page open. The video streams to your computer.
                    </p>
                )}
            </div>
        </div>
    )
}

const styles = {
    page: {
        // Fixed-height scroll container (not min-height) so the page ALWAYS
        // scrolls on its own — otherwise the troubleshooting steps get clipped
        // below the fold when an ancestor doesn't scroll.
        height: '100dvh',
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        background: '#0d0d14',
        color: '#fff',
        fontFamily: 'sans-serif',
        padding: '16px',
        boxSizing: 'border-box',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        // Keep it scrollable but hide the scrollbar chrome (Firefox / IE); the
        // WebKit rule is injected via a <style> tag below.
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
    },
    card: {
        width: '100%',
        maxWidth: 420,
        textAlign: 'center',
        paddingBottom: 24,
        flexShrink: 0,
    },
    title: { margin: '0 0 4px', fontSize: 20 },
    room: { margin: '0 0 12px', color: '#aab', fontSize: 14 },
    videoWrap: {
        position: 'relative',
        width: '100%',
        aspectRatio: '3 / 4',
        maxHeight: '46dvh',
        background: '#000',
        borderRadius: 14,
        overflow: 'hidden',
        margin: '0 auto',
    },
    video: { width: '100%', height: '100%', objectFit: 'cover' },
    dot: {
        position: 'absolute',
        top: 10,
        right: 10,
        width: 12,
        height: 12,
        borderRadius: '50%',
    },
    status: { margin: '14px 0 4px', fontSize: 15, fontWeight: 600 },
    subStatus: { margin: '0 0 8px', color: '#e0a24c', fontSize: 13 },
    reason: {
        margin: '0 0 12px',
        color: '#f0b3b3',
        fontSize: 13,
        lineHeight: 1.4,
    },
    hint: { color: '#889', fontSize: 13, marginTop: 12 },
    btnRow: {
        display: 'flex',
        gap: 10,
        justifyContent: 'center',
        flexWrap: 'wrap',
    },
    btn: {
        border: 'none',
        borderRadius: 24,
        padding: '14px 28px',
        fontSize: 16,
        fontWeight: 600,
        background: '#f4813f',
        color: '#fff',
        cursor: 'pointer',
    },
    btnAlt: {
        border: '1px solid #556',
        borderRadius: 24,
        padding: '12px 22px',
        fontSize: 15,
        fontWeight: 600,
        background: 'transparent',
        color: '#fff',
        cursor: 'pointer',
    },
    btnDanger: {
        border: '1px solid #7a3b3b',
        borderRadius: 24,
        padding: '12px 22px',
        fontSize: 15,
        fontWeight: 600,
        background: 'transparent',
        color: '#f0a3a3',
        cursor: 'pointer',
    },
    helpToggle: {
        display: 'block',
        margin: '14px auto 0',
        border: 'none',
        background: 'transparent',
        color: '#8ab4ff',
        fontSize: 14,
        textDecoration: 'underline',
        cursor: 'pointer',
    },
    help: {
        marginTop: 12,
        textAlign: 'left',
        background: '#171722',
        border: '1px solid #2a2a3a',
        borderRadius: 12,
        padding: '12px 14px',
    },
    helpTitle: { margin: '0 0 8px', fontSize: 14, fontWeight: 700 },
    helpList: { margin: 0, paddingLeft: 20 },
    helpItem: { fontSize: 13, lineHeight: 1.5, color: '#cdd', marginBottom: 6 },
}

export default MobileCamera